/**
 * Holding a share back, where a campaign is scheduled — slice 61.
 *
 * One choice: hold nobody back (the default), or 5, 10 or 20 percent at random,
 * so Sense can compare who converted in each group. Held-back people get a
 * recipient row marked Held back — visible and counted, never silently dropped.
 *
 * ## THE NUMBER, NOT THE ARITHMETIC
 *
 * Beside the choice: how many people it holds back — the preview's own count
 * (`heldBack`, by the hash and the rules materialisation uses), so a sender
 * never has to work out that 5% of their list is 12 people. Under the readout's
 * floor it says "Too few to measure" — a choice that cannot produce a reading is
 * not offered as if it could (`SchedulePressureNotice`'s rule, applied to a
 * number instead of a switch).
 *
 * On the surface: one line, that held-back people get nothing from this
 * campaign — the fact a bank must not miss before withholding a notice. How the
 * comparison works sits behind `MoreDetail` (slice 38's rule).
 *
 * ## WHEN IT DRAWS NOTHING
 *
 * When the plan does not include Sense: the readout is a Sense screen, and a
 * pillar a workspace has not got does not leak onto a Reach screen. Whenever the
 * control is not drawn the choice is reset to nobody, so a share picked before
 * the entitlement changed cannot go on holding people back from a screen that no
 * longer shows it.
 *
 * ## WHY ONLY THREE SHARES
 *
 * Below 5% a typical cooperative bank's list leaves fewer people held back than
 * the readout needs; above 20% the campaign withholds more than a sender will
 * accept to learn one number. The database allows up to 50 for a later slice.
 */
import { useEffect } from 'react';

import { HOLDOUT_MIN_PEOPLE, HOLDOUT_PERCENT_VALUES, type HoldoutPercent } from '@wend/shared';
import { FormField, Select, StateChip } from '@wend/ui';

import { useAuth } from '../../auth/auth-context';
import { formatCount } from '../../lib/format';
import { MoreDetail } from '../templates/MoreDetail';

export interface ScheduleHoldoutControlProps {
  /** 0 is nobody. */
  readonly percent: number;
  readonly onPercentChange: (value: number) => void;
  /** The preview's count for the chosen share; null while it is being read. */
  readonly heldBack: number | null;
  readonly disabled: boolean;
}

const NOBODY = 'nobody';

export function ScheduleHoldoutControl(props: ScheduleHoldoutControlProps) {
  const { user } = useAuth();
  const entitled = user !== null && user.features.includes('sense');
  const { percent, onPercentChange } = props;
  useEffect(() => {
    if (!entitled && percent !== 0) onPercentChange(0);
  }, [entitled, percent, onPercentChange]);
  if (!entitled) return null;
  return <HoldoutChoice {...props} />;
}

/** The control itself, without the entitlement check — exported for the render suite. */
export function HoldoutChoice({ percent, onPercentChange, heldBack, disabled }: ScheduleHoldoutControlProps) {
  const options = [
    { value: NOBODY, label: 'Nobody' },
    ...HOLDOUT_PERCENT_VALUES.map((share: HoldoutPercent) => ({ value: String(share), label: `${String(share)}% at random` })),
  ];

  return (
    <div className="flex max-w-md flex-col gap-1.5" data-role="schedule-holdout">
      <FormField label="Hold back to measure" description="Held-back people get nothing from this campaign.">
        <Select
          value={percent === 0 ? NOBODY : String(percent)}
          onValueChange={(value) => onPercentChange(value === NOBODY ? 0 : Number(value))}
          options={options}
          disabled={disabled}
        />
      </FormField>
      {percent > 0 && heldBack !== null && (
        <p className="flex flex-wrap items-center gap-2 text-[0.82rem] text-muted" data-role="schedule-holdout-count">
          Holds back {formatCount(heldBack)} {heldBack === 1 ? 'person' : 'people'}
          {heldBack < HOLDOUT_MIN_PEOPLE && <StateChip state="under_floor" label="Too few to measure" />}
        </p>
      )}
      <MoreDetail summary="How holding back works">
        <p>
          The people held back are chosen at random. Some days after the campaign, Sense compares who converted
          among the people sent it with the people held back. Fewer than {String(HOLDOUT_MIN_PEOPLE)} held back
          gives no reading. Do not hold back a notice people must receive.
        </p>
      </MoreDetail>
    </div>
  );
}
